
import { Component, forwardRef, OnDestroy } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    NonNullableFormBuilder,
    ReactiveFormsModule,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { BrandingColors } from '../../models/branding.model';
import { ColorPickerComponent } from '../color-picker/color-picker.component';
import { ColorPaletteComponent } from '../palettes/color-palette.component';
import { ToneSelectorFormComponent } from '../tone-selector/tone-selector-form.component';
import { BrandingColorFormGroupType } from './models/branding-form-types';

@Component({
    selector: 'app-branding-colors-form',
    imports: [
    ReactiveFormsModule,
    ColorPickerComponent,
    ColorPaletteComponent,
    ToneSelectorFormComponent
],
    templateUrl: './branding-colors-form.component.html',
    styleUrls: ['./branding-colors-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BrandingColorsFormComponent),
            multi: true,
        },
    ]
})
export class BrandingColorsFormComponent implements ControlValueAccessor, OnDestroy {
    public form: BrandingColorFormGroupType;
    private unsubscribe$ = new Subject<void>();
    private onChange: (value: BrandingColors) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private fb: NonNullableFormBuilder) {
        this.form = this.createForm();
        this.setupFormSubscription();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private createForm(): BrandingColorFormGroupType {
        return this.fb.group({
            primary: this.fb.group({
                color: new FormControl('#016C78', { nonNullable: true }),
                tones: this.fb.control({
                    lightTone: 95,
                    primaryTone: 40,
                    darkTone: 20,
                }),
            }),
            secondary: this.fb.group({
                color: new FormControl('#0C2245', { nonNullable: true }),
                tones: this.fb.control({
                    lightTone: 95,
                    primaryTone: 40,
                    darkTone: 20,
                }),
            }),
            success: this.fb.group({
                color: new FormControl('#4caf50', { nonNullable: true }),
                tones: this.fb.control({
                    lightTone: 95,
                    primaryTone: 40,
                    darkTone: 20,
                }),
            }),
            error: this.fb.group({
                color: new FormControl('#f44336', { nonNullable: true }),
                tones: this.fb.control({
                    lightTone: 95,
                    primaryTone: 40,
                    darkTone: 20,
                }),
            }),
            warn: this.fb.group({
                color: new FormControl('#fcb900', { nonNullable: true }),
                tones: this.fb.control({
                    lightTone: 95,
                    primaryTone: 40,
                    darkTone: 20,
                }),
            }),
            info: this.fb.group({
                color: new FormControl('#143981', { nonNullable: true }),
                tones: this.fb.control({
                    lightTone: 95,
                    primaryTone: 40,
                    darkTone: 20,
                }),
            }),
        });
    }

    private setupFormSubscription(): void {
        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            this.onChange(value as BrandingColors);
            this.onTouched();
        });
    }

    writeValue(value: BrandingColors): void {
        if (value) {
            this.form.patchValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: (value: BrandingColors) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    get primaryGroup(): FormGroup {
        return this.form.get('primary') as FormGroup;
    }

    get primaryColorControl(): FormControl<string> {
        return this.primaryGroup.get('color') as FormControl<string>;
    }

    get secondaryGroup(): FormGroup {
        return this.form.get('secondary') as FormGroup;
    }

    get secondaryColorControl(): FormControl<string> {
        return this.secondaryGroup.get('color') as FormControl<string>;
    }

    get successGroup(): FormGroup {
        return this.form.get('success') as FormGroup;
    }

    get successColorControl(): FormControl<string> {
        return this.successGroup.get('color') as FormControl<string>;
    }

    get errorGroup(): FormGroup {
        return this.form.get('error') as FormGroup;
    }

    get errorColorControl(): FormControl<string> {
        return this.errorGroup.get('color') as FormControl<string>;
    }

    get warnGroup(): FormGroup {
        return this.form.get('warn') as FormGroup;
    }

    get warnColorControl(): FormControl<string> {
        return this.warnGroup.get('color') as FormControl<string>;
    }

    get infoGroup(): FormGroup {
        return this.form.get('info') as FormGroup;
    }

    get infoColorControl(): FormControl<string> {
        return this.infoGroup.get('color') as FormControl<string>;
    }
}
